import { fetchQuery } from '@/api/chatbot';
import AgentChatDropdown from '@/components/controls/agent-chat-dropdown';
import { useAppStore } from '@/store/appStore';
import { useLanguageStore } from '@/store/languageStore';
import { useNavigationStore } from '@/store/navigationStore';
import React, { useEffect, useRef, useState } from 'react';

interface ChatEntry {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  streaming?: boolean;
}

const URL_PATTERN = /(https?:\/\/[^\s]+)/g;
const TIME_FORMATTER = new Intl.DateTimeFormat([], {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const ChatBotPage: React.FC = () => {
  const [chatHistory, setChatHistory] = useState<ChatEntry[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const appLanguage = useLanguageStore((state) => state.appLanguage);
  const chatAgentEndpoint = useNavigationStore((state) => state.chatAgentEndpoint);
  const chatAgentVenueId = useNavigationStore((state) => state.chatAgentVenueId);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const sendMessage = async () => {
    const userQuery = inputValue.trim();
    if (!userQuery || isStreaming) return;

    // Resolve IDs through the stores at call time
    const trace = useNavigationStore.getState().ensureTraceId();
    const user = useAppStore.getState().ensureUserId();
    console.log("sendMessage traceId:", trace, "userId:", user);

    // Add user bubble only — assistant bubbles are created per update chunk
    setChatHistory((prev) => [
      ...prev,
      { role: 'user', content: userQuery, timestamp: TIME_FORMATTER.format(new Date()) },
    ]);
    setInputValue('');
    setIsStreaming(true);

    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    const response = await fetchQuery({
      userQuery,
      traceId: trace,
      userId: user,
      venueId: chatAgentVenueId,
      mapId: "",
      is_disability: "0",
      lang: "en",
    }, chatAgentEndpoint || undefined)

    if (response) {

      setChatHistory((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: response.message,
          timestamp: TIME_FORMATTER.format(new Date()),
          streaming: false,
        },
      ]);

      if (response.nav && response.navLink) {
        setChatHistory((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `Here is the Live AI route: ${response.navLink}`,
            timestamp: TIME_FORMATTER.format(new Date()),
            streaming: false,
          },
        ]);
      }

      setIsStreaming(false);
    }

  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderMessageContent = (entry: ChatEntry) => {
    if (entry.role !== 'assistant') return entry.content;
    console.log("entry.content", entry.content)
    const parts = entry.content.split(URL_PATTERN);
    return parts.map((part, index) => {
      if (!part.match(/^https?:\/\//)) return part;

      return (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.link}
        >
          {part}
        </a>
      );
    });
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.headerTitle}>Assistant</span>
        </div>

        <span style={{ ...styles.statusBadge, ...(isStreaming ? styles.statusActive : styles.statusIdle) }}>
          {isStreaming ? 'Responding...' : 'Ready'}
        </span>
      </div>

      {/* Messages */}
      <div style={styles.messageArea}>
        {chatHistory.length === 0 && (
          <div style={styles.emptyState}>Ask me anything to get started.</div>
        )}

        {chatHistory.map((entry, index) => (
          <div
            key={index}
            style={{
              ...styles.messageRow,
              justifyContent: entry.role === 'user' ? 'flex-end' : 'flex-start',
              alignItems: entry.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div style={{ ...styles.messageGroup, alignItems: entry.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={styles.messageTime}>{entry.timestamp}</div>
              <div style={{ ...styles.bubble, ...(entry.role === 'user' ? styles.userBubble : styles.assistantBubble) }}>
                {renderMessageContent(entry)}
                {entry.streaming && <span style={styles.cursor}></span>}
              </div>
            </div>
          </div>
        ))}
        {isStreaming && (
          <div style={{ ...styles.messageRow, justifyContent: 'flex-start', alignItems: 'flex-start' }}>
            <div style={{ ...styles.messageGroup, alignItems: 'flex-start' }}>
              <div style={styles.messageTime}>{TIME_FORMATTER.format(new Date())}</div>
              <div style={{ ...styles.bubble, ...styles.assistantBubble, ...styles.typingBubble }}>...</div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={styles.inputArea}>
        <textarea
          style={styles.input}
          placeholder="Type your message"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={isStreaming}
        />
        <div><AgentChatDropdown disabled={isStreaming} /></div>
        <button
          style={{ ...styles.sendButton, ...(isStreaming || !inputValue.trim() ? styles.sendButtonDisabled : {}) }}
          onClick={sendMessage}
          disabled={isStreaming || !inputValue.trim()}
        >
          {isStreaming ? '...' : '↑'}
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    maxWidth: '720px',
    margin: '0 auto',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    backgroundColor: '#f9f9f9',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: '1px solid #e5e5e5',
    backgroundColor: '#fff',
    gap: '12px',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  headerTitle: { fontWeight: 600, fontSize: '16px', color: '#111' },
  statusBadge: { fontSize: '12px', padding: '3px 10px', borderRadius: '999px', fontWeight: 500 },
  statusIdle: { backgroundColor: '#f0f0f0', color: '#666' },
  statusActive: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
  messageArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  emptyState: { textAlign: 'center', color: '#aaa', fontSize: '14px', marginTop: '40px' },
  messageRow: { display: 'flex', width: '100%' },
  messageGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  messageTime: { fontSize: '11px', color: '#888', padding: '0 4px' },
  bubble: {
    maxWidth: '75%',
    padding: '10px 14px',
    borderRadius: '16px',
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  userBubble: { backgroundColor: '#111', color: '#fff', borderBottomRightRadius: '4px' },
  assistantBubble: { backgroundColor: '#fff', color: '#111', border: '1px solid #e5e5e5', borderBottomLeftRadius: '4px' },
  typingBubble: { whiteSpace: 'nowrap', wordBreak: 'normal', display: 'inline-block' },
  link: { color: '#1565c0', textDecoration: 'underline' },
  cursor: { display: 'inline-block', marginLeft: '2px' },
  inputArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    borderTop: '1px solid #e5e5e5',
    backgroundColor: '#fff',
  },
  input: {
    flex: 1,
    resize: 'none',
    border: '1px solid #ddd',
    borderRadius: '12px',
    padding: '10px 14px',
    fontSize: '14px',
    fontFamily: 'inherit',
    outline: 'none',
    lineHeight: '1.5',
    backgroundColor: '#f9f9f9',
  },
  sendButton: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#111',
    color: '#fff',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sendButtonDisabled: { backgroundColor: '#ccc', cursor: 'not-allowed' },
};

export default ChatBotPage;